import { useMutation } from '@apollo/client';
import type { RemoveParentControlsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { RemoveParentControlsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteParentControlsTRPC } from './useDeleteParentControlsTRPC';

export const useDeleteParentControls = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteParentControlsGraphQL, graphqlState] = useMutation(
    RemoveParentControlsDocument,
    {
      update: (cache) => {
        evictField(cache, 'control');
        evictField(cache, 'control_group');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteParentControlsTRPC();

  const deleteParentControls = async (
    parentId: string,
    controlIds: string[]
  ): Promise<RemoveParentControlsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteParentControls(parentId, controlIds);
    }

    const result = await deleteParentControlsGraphQL({
      variables: { ParentId: parentId, ControlIds: controlIds },
    });
    if (!result.data) {
      throw new Error('Failed to delete parent controls');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteParentControls,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteParentControls,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
